import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import {
  Breadcrumb,
  Card,
  Col,
  Container,
  Row,
} from "react-bootstrap";

import {
  CustomPaging,
  EditingState,
  PagingState,
  SortingState,
  SearchState,
} from '@devexpress/dx-react-grid';

import {
  Grid,
  PagingPanel,
  Table,
  TableHeaderRow,
  TableEditColumn,
  Toolbar,
  SearchPanel
} from '@devexpress/dx-react-grid-bootstrap4';
import {Loading} from 'components/Loading/loading';
import { useAlert, TYPES } from 'hooks/use-alert-hook.js';

const SORT_FIELD_MAP = {
  SCOPE_ID: 'scope.SCOPE_NAME',
  DEPT_ID: 'department.DEPT_NAME',
}

export default function AuditeeIndex() {

  const history = useHistory();
  const [swal, setSwal] = useAlert();
  const [columns] = useState([
    { name: 'AUDITEE_ID', title: 'ID' },
    { name: 'SCOPE_ID', title: 'Scope', getCellValue: row => (row.scope) ? row.scope.SCOPE_NAME : `n-a` },
    { name: 'DEPT_ID', title: 'Department', getCellValue: row => (row.department) ? row.department.DEPT_NAME : `n-a` },
  ]);
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [pageSizes] = useState([5, 10, 15]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([{ columnName: 'AUDITEE_ID', direction: 'asc' }]);
  const [searchValue, setSearchValue] = useState('');

  const commitChanges = async ({ deleted }) => {

    try {

      if (deleted) {
        const item = rows.find(row => row.AUDITEE_ID === [...deleted].pop());
        const onSuccessCallback = async () => {
          const response = await axios.delete(`${process.env.REACT_APP_API_HOST}auditees/${item.AUDITEE_ID}`);
          if (response.status !== 204) throw new Error('Update error');
          return fetchData();
        }

        setSwal({
          type: TYPES.CONFIRM,
          payload: {
            title: `Are you sure`,
            message: `You are about to delete "${item.scope.SCOPE_NAME + ' - ' + item.department.DEPT_NAME}" auditee?`,
            onSuccess: {
              title: `Success`,
              message: `Delete Success`,
              callback: onSuccessCallback,
            }
          }
        });
      }

    } catch (error) {

      setSwal({
        type: TYPES.ERROR,
        payload: {
          message: error.toString(),
        }
      });

     }

  };

  const getRowId = row => row.AUDITEE_ID;

  const fetchData = async () => {

    setLoading(true);

    try {

      const orderBy = sorting?.[0];
      const response = await axios.get(`${process.env.REACT_APP_API_HOST}auditees`, {
        params: {
          expand: 'scope,department',
          page: currentPage + 1,
          'per-page': pageSize,
          'search': searchValue,
          sort: `${orderBy?.direction === 'desc' ? '-' : ''}${SORT_FIELD_MAP[orderBy?.columnName] ?? orderBy?.columnName}`
        }
      });

      if (response.status !== 200) throw new Error(`Fetch error`);

      setRows(response.data.items);
      setTotalCount(response.data._meta.totalCount);

    } catch (error) {
      setSwal({
        type: TYPES.ERROR,
        payload: {
          message: error.toString(),
        }
      });
    }

    setLoading(false);
  }

  useEffect(() => fetchData(), [searchValue, pageSize, currentPage, sorting]);

  useEffect(() => {

    if (editingRowIds && editingRowIds.length > 0) {
      return history.push(`/admin/auditee/${editingRowIds.pop()}`);
    }

  }, [editingRowIds]);

  return (
    <>
      {swal}
      <Container fluid>
        <Breadcrumb>
          <Breadcrumb.Item href="#pablo" onClick={(e) => e.preventDefault()}>
            Master
          </Breadcrumb.Item>
          <Breadcrumb.Item href="#pablo" onClick={(e) => e.preventDefault()}>
            Auditee
          </Breadcrumb.Item>
          <Breadcrumb.Item aria-current="page" active>
            Index
          </Breadcrumb.Item>
        </Breadcrumb>
        {rows && (
          <Row>
            <Col md="12">
              <Card>
                <Card.Header>
                  <Card.Title as="h4">Auditee List</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Grid
                    rows={rows}
                    columns={columns}
                    getRowId={getRowId}
                  >
                    <SearchState onValueChange={setSearchValue} />

                    <SortingState
                      sorting={sorting}
                      onSortingChange={setSorting}
                    />
                    <PagingState
                      currentPage={currentPage}
                      onCurrentPageChange={setCurrentPage}
                      pageSize={pageSize}
                      onPageSizeChange={setPageSize}
                    />
                    <CustomPaging totalCount={totalCount} />
                    <EditingState
                      editingRowIds={editingRowIds}
                      onEditingRowIdsChange={setEditingRowIds}
                      onCommitChanges={commitChanges}
                    />
                    <Table />
                    <TableHeaderRow showSortingControls />
                    <TableEditColumn
                      showAddCommand
                      showEditCommand
                      showDeleteCommand
                      commandComponent={CommandComponent}
                    />
                    <PagingPanel
                      pageSizes={pageSizes}
                    />
                    <Toolbar />
                    <SearchPanel />
                    {loading && <Loading />}
                  </Grid>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
}


function CommandComponent({ id, text, onExecute }) {

  switch (id) {
    case 'add':
      return (
        <Link to="/admin/auditee/create">
          <button type="button" className="btn btn-link dx-g-bs4-table-edit-command-cell" id={id}>{text}</button>
        </Link>
      );
    default:
      return <button type="button" onClick={onExecute} className="btn btn-link dx-g-bs4-table-edit-command-cell" id={id}>{text}</button>
  }
}